// useSelectedSite — hash-driven selection state for the Projects view.
//
// Truth source: `window.location.hash`. The hook parses `#/projects/:siteId`,
// exposes {selectedSiteId, selectSite}, and syncs across components via the
// browser's `hashchange` event (so back/forward buttons Just Work).
//
// selectSite() uses history.replaceState — a pin click is not a navigation
// history entry. Deep links and refreshes still resolve correctly because
// the hook rereads the hash on every mount.
//
// If the current hash has no siteId, the hook defaults to the first project
// in the list (which may arrive async — the hook re-runs when projects change
// and locks in a default the first time the list becomes non-empty).

import { useEffect, useState, useCallback } from 'react';

const HASH_MATCH = /^#\/projects\/([^/?#]+)/;

function readHash() {
  const h = typeof window !== 'undefined' ? window.location.hash : '';
  const m = HASH_MATCH.exec(h || '');
  return m ? decodeURIComponent(m[1]) : null;
}

// Only ever rewrite the hash while we are actually on a Projects route.
// Without this guard, leaving Projects (header "Map" link → #/) fired
// hashchange → selectedSiteId became null → the default-selection effect
// wrote #/projects/<first id> straight back, and the Map view never
// appeared (2026-10-05).
const onProjectsRoute = () =>
  typeof window !== 'undefined' && /^#\/projects(\/|\?|$)/.test(window.location.hash || '');
function writeHash(id) {
  if (typeof window === 'undefined') return;
  if (!onProjectsRoute()) return;
  const next = id ? `#/projects/${encodeURIComponent(id)}` : '#/projects';
  // Same hash → no-op (prevents needless hashchange firing on re-select).
  if (window.location.hash === next) return;
  window.history.replaceState(null, '', next);
  // history.replaceState does NOT fire hashchange. Fire it manually so any
  // other listener in this window (e.g. VanirApp.jsx's route matcher) reacts.
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

/**
 * @param {Array<{id: string}>} projects — the fetched project list
 * @returns {{
 *   selectedSiteId: string | null,
 *   selectSite: (id: string) => void,
 * }}
 */
export function useSelectedSite(projects) {
  const [selectedSiteId, setSelectedSiteId] = useState(() => readHash());

  // Sync when the hash changes elsewhere (back/forward, another component).
  useEffect(() => {
    const onHashChange = () => setSelectedSiteId(readHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Lock in a default once projects arrive AND no site is currently selected.
  useEffect(() => {
    if (selectedSiteId) return;
    if (!Array.isArray(projects) || projects.length === 0) return;
    const first = projects[0].id;
    if (first) {
      writeHash(first);
      setSelectedSiteId(first);
    }
  }, [projects, selectedSiteId]);

  // Guard: if the currently-selected id disappears from the list (deletion),
  // fall back to the first project. Don't wipe the hash — the id might come
  // back on refetch. Only replace when we're sure it's gone from a
  // fully-loaded list.
  useEffect(() => {
    if (!selectedSiteId) return;
    if (!Array.isArray(projects) || projects.length === 0) return;
    const stillThere = projects.some(p => p.id === selectedSiteId);
    if (!stillThere) {
      const first = projects[0].id;
      writeHash(first);
      setSelectedSiteId(first);
    }
  }, [projects, selectedSiteId]);

  const selectSite = useCallback((id) => {
    writeHash(id);
    setSelectedSiteId(id);
  }, []);

  return { selectedSiteId, selectSite };
}
